import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listOwnedAssets } from "../api/atomicApi";
import { redeemBackedAsset } from "../api/actions";
import { AssetCard } from "../components/AssetCard";
import { useWallet } from "../wallet/WalletContext";
import { getBackingInfo } from "../utils/backing";
import { attachEscrowBacking } from "../api/nftdropsApi";
import type { AtomicAsset } from "../types";
import { showError, confirmAction } from "../components/Dialog";

export function Inventory() {
  const { session, account, connect, connecting } = useWallet();
  const [assets, setAssets] = useState<AtomicAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!account) {
      setAssets([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listOwnedAssets({ owner: account })
      .then((data) => attachEscrowBacking(data))
      .then((data) => {
        if (!cancelled) setAssets(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load your assets");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [account]);

  async function handleRedeem(asset: AtomicAsset) {
    if (!session) return;
    const backing = getBackingInfo(asset);
    if (
      !(await confirmAction({
        title: "Redeem this backed NFT?",
        message: (
          <>
            This <strong>permanently burns</strong> the NFT and releases{" "}
            <strong>
              {backing?.amount} {backing?.symbol}
            </strong>{" "}
            from escrow to your wallet. This can't be undone.
          </>
        ),
        confirmLabel: "Burn & redeem",
        danger: true,
      }))
    )
      return;
    setBusyId(asset.asset_id);
    try {
      await redeemBackedAsset(session, asset.asset_id);
      setAssets((prev) => prev.filter((a) => a.asset_id !== asset.asset_id));
    } catch (err) {
      showError(err, "Redeem failed");
    } finally {
      setBusyId(null);
    }
  }

  if (!account) {
    return (
      <div className="page state-message">
        <p>Connect your wallet to see your assets.</p>
        <button className="btn btn--primary" onClick={connect} disabled={connecting}>
          {connecting ? "Connecting…" : "Connect Wallet"}
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__header">
        <h1>My Assets</h1>
        <p className="page__subtitle">
          Assets owned by <strong>{account}</strong>. Backed items can be redeemed here — burning releases the
          locked tokens straight back to your wallet.
        </p>
      </div>

      {loading && <div className="state-message">Loading…</div>}
      {error && <div className="state-message state-message--error">{error}</div>}
      {!loading && !error && assets.length === 0 && (
        <div className="state-message">
          No assets found. <Link to="/mint">Mint a backed NFT</Link> or browse the{" "}
          <Link to="/">marketplace</Link>.
        </div>
      )}

      <div className="asset-grid">
        {assets.map((asset) => {
          const backing = getBackingInfo(asset);
          return (
            <AssetCard
              key={asset.asset_id}
              asset={asset}
              actionLabel={backing ? (busyId === asset.asset_id ? "Redeeming…" : "Redeem") : undefined}
              actionDisabled={busyId === asset.asset_id}
              onAction={backing ? () => handleRedeem(asset) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}
